/**
 * SavedRitualsTabContent Component
 * Content for Saved Rituals tab in profile detail view
 * Shows rituals the user has saved/bookmarked
 */

import { Search, X } from "lucide-react"
import { useState } from "react"
import { RitualCard } from "@/components/cards/ritualCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TabEmptyState } from "@/features/my-rituality/components/TabEmptyState"
import { formatRitualType } from "@/features/rituals/types"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { useSavedRituals, useToggleRitualSave } from "../hooks/useSavedRituals"
import type { SavedRitual } from "../types/saved.types"

interface SavedRitualsTabContentProps {
  className?: string
  searchQuery?: string
}

export function SavedRitualsTabContent({ className, searchQuery }: SavedRitualsTabContentProps) {
  const { t } = useTranslation("myRituality")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const debouncedSearch = useDebounce(localSearch || searchQuery || "", 500)

  const { data, isLoading, isError } = useSavedRituals({
    limit: 50,
    search: debouncedSearch || undefined,
  })

  const toggleRitualSave = useToggleRitualSave()

  // Transform API data to match RitualCardProps
  const rituals =
    data?.rituals.map((item: SavedRitual) => ({
      id: item.ritual.id,
      image:
        item.ritual.uploads?.find((u: { isCover: boolean }) => u.isCover)?.filePath || undefined,
      userAvatar: item.ritual.creator?.coverImage ?? undefined,
      username: item.ritual.creator?.name ?? "Unknown",
      userHandle: item.ritual.creator?.username ? `@${item.ritual.creator.username}` : "",
      title: item.ritual.title,
      tags: [item.ritual.participationFee, formatRitualType(item.ritual.type)].filter(
        (tag): tag is string => tag !== null,
      ),
      duration: `${item.ritual.duration ?? 0} min`,
      contextualTag: formatRitualType(item.ritual.type),
      isBookmarked: item.isSaved,
      userId: item.ritual.creator?.id,
      onBookmarkClick: () => toggleRitualSave.mutate(item.ritual.id),
    })) || []

  const totalCount = data?.total ?? 0

  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              Saved Rituals ({totalCount})
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          <div className="text-sm leading-6.25 md:hidden">
            <span className="mt-1.25 font-normal">Rituals you bookmarked to explore later.</span>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-stone">Loading saved rituals...</p>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="mt-4 flex items-center justify-center py-8">
          <p className="text-sm text-red">Error loading saved rituals</p>
        </div>
      )}

      {/* Rituals Grid */}
      {!isLoading && !isError && (
        <div className="mt-7">
          {rituals.length === 0 ? (
            <div className="pb-6">
              {searchQuery ? (
                <p className="text-sm text-primary/50">{t("savedTab.noResults")}</p>
              ) : (
                <TabEmptyState
                  title={t("savedTab.emptyTitle")}
                  description={t("savedTab.emptyDescription")}
                  ctaLabel={t("savedTab.exploreRituals")}
                  ctaTo="/learn-practice"
                />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rituals.map(ritual => (
                <div key={ritual.id} className="h-[260px] md:h-[500px]">
                  <RitualCard {...ritual} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
